import React, { useState, useEffect } from 'react'
import { Transition } from 'react-transition-group'
import styled from 'styled-components'
import { css } from '@styled-system/css'

import { Flex, P, RangeSlider, Switch, SVGImage } from 'ui/bend'
import { MusicVolumeIcon } from 'images/bend'
import { MediaForward, MediaForwardDisabled } from 'images/bend'
import { useVideoPlayerContext } from '../VideoPlayerContext'
import uuidv4 from 'uuid/v4'
import { axios } from 'api'

import {
  MusicContainer,
  MusicInfoBlock,
  FeedFMCopyright
} from './_musicPlayerStyles'
import { useMusicContext } from './MusicPlayerContext'
import { FeedFMDisclimer } from './FeedFMDisclimer'
import MusicMarquee from './MusicMarquee'
import { InfoIcon } from 'components_v2/Icons'
import { useModal_deprecated } from 'hooks'

const BodyText = styled(P)(() =>
  css({
    width: '57%',
    textAlign: 'left',
    whiteSpace: 'normal',
    fontFamily: 'Proxima-Nova',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '16px',
    lineHeight: '140%',
    margin: '30px 0 40px 0'
  })
)

const SkipButtonWrapper = styled.div`
  &:active {
    opacity: 0.2;
  }
`
const MusicPlayerCard = () => {
  const {
    currentSong,
    handleUserVolumeChange,
    shouldPlayMusic,
    toggleShouldPlay,
    userVolume,
    showMarquee,
    marqueeExiting,
    skipMusic
  } = useMusicContext()

  const { state } = useVideoPlayerContext()
  const isVideoPlayerIsPlay = state.playerSatus !== 'pause'

  const [skipError, setSkipError] = useState(false)
  const [clickedTimes, setClickedTimes] = useState(0)
  const modal = useModal_deprecated()

  /*
   * Our CSS-in-JS is kinda' wonky for this component because it will (for now) always be embedded within VideoJS.
   * VideoJS mainly uses CSS classes to indicate changes in state, we need to rely on the structure of CSS to work for us rather than against us.
   * What this means is that lots of animations are handled in the parent component (i.e. VideoPlayerContainer inside of _videoPlayerStyles.js)
   * This is especially true because there are multiple, intersecting states (.vjs-user-active/inactive, .vjs-has-started, .vjs-playing).
   *
   * Therefore, we use classNames rather than state or CSS-in-JS props to signal these things
   * .show-marque, .music-toggle-switch, and .music-volume-slider classs let this  higher-up stylesheet how we want to display music card
   * We MUST display the song information when the first song plays for contractual reasons -- CONTRACT REQUIREMENT !!
   *
   * As a result, please be aware that animation is broken between BOTH _videoPlayerStyles.js and _musicPlayerStyles.js
   */

  /* If we find ourselves doing more chained className stuff, we might want to make a helper or use classNames */
  const musicContainerClassName = () => {
    // If music shouldn't be playing, don't add any classnames.
    // We do this so that way if a user turns off music while either showMarquee or marqueeExiting is true, we can immediately bail out and not show any UI.
    if (!shouldPlayMusic || !currentSong) return 'no_music_playing'

    if (showMarquee) {
      // We want marqueeExiting to override showMarquee, so that it can gracefully exit.
      if (marqueeExiting) return 'marquee-exiting'
      return 'show-marquee'
    }
  }

  const openModal = () => {
    modal.open()
  }

  const skipMusicFunc = () => {
    const isSkipped = skipMusic()
    if (!isSkipped) {
      setSkipError(true)
      setTimeout(() => {
        setSkipError(false)
      }, 3000)
    }

    // passing skip data to GA4 event
    window.dataLayer?.push({
      event: 'music_skipped',
      status: !isSkipped ? 'fail' : 'success'
    })
  }

  useEffect(() => {
    setClickedTimes(clickedTimes + 1)
    if (state.meta?.videoOriginType && clickedTimes >= 1) {
      // We need a new client_uuid every post request.
      const client_uuid = uuidv4()
      const identifier =
        state.meta.videoOriginType === 'Plan'
          ? state.meta.planId
          : state.meta.planEntryId
      const postBody = {
        id: identifier,
        video_origin_type: 'MusicVideo',
        video_origin_subtype: state.meta.videoOriginType,
        occurred_at: new Date().toUTCString(),
        client_uuid,
        state: shouldPlayMusic ? 'on' : 'off'
      }

      axios
        .post('video_events/handle_video_event', postBody)
        .then((res) => {
          if (res.data.ga4Event) {
            window.dataLayer.push(res.data.ga4Event)
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }, [shouldPlayMusic])

  return (
    <>
      <MusicContainer
        expand={shouldPlayMusic}
        className={musicContainerClassName()}
      >
        <Flex justifyContent='space-between' className='music-toggle-switch'>
          {/* We set margin to 0 here to override application.css in legacy pages */}
          <div>
            <P
              color='black'
              pr='12px'
              m='0px'
              fontSize='14px !important'
              fontWeight='400 !important'
              lineHeight='115% !important'
            >
              Music
            </P>
            {shouldPlayMusic && (
              <FeedFMCopyright onClick={openModal}>
                <span>Powered by Feed.fm</span>

                <InfoIcon width='12px' height='12px' color='#636363' />
              </FeedFMCopyright>
            )}
          </div>
          <Switch
            label='Toggle Music'
            name='toggleMusic'
            id='toggleMusic'
            checked={shouldPlayMusic}
            onChange={toggleShouldPlay}
          />
        </Flex>
        <Transition
          in={shouldPlayMusic}
          timeout={300}
          mountOnEnter
          unmountOnExit
        >
          {(state) => (
            <MusicInfoBlock state={state} className='music-info-block'>
              <MusicMarquee skipError={skipError} />
              <Flex alignItems='center' className='music-volume-slider'>
                <SkipButtonWrapper>
                  <SVGImage
                    onClick={isVideoPlayerIsPlay ? skipMusicFunc : null}
                    style={{
                      cursor: isVideoPlayerIsPlay ? 'pointer' : 'not-allowed'
                    }}
                    SVG={
                      isVideoPlayerIsPlay ? MediaForward : MediaForwardDisabled
                    }
                    mr='16px'
                  />
                </SkipButtonWrapper>

                <SVGImage SVG={MusicVolumeIcon} mr='16px' />
                <RangeSlider
                  min={0}
                  max={100}
                  value={userVolume}
                  ml='16px'
                  id='musicVolume'
                  label='Music Volume'
                  onChange={handleUserVolumeChange}
                />
              </Flex>
              <FeedFMDisclimer modal={modal} />
            </MusicInfoBlock>
          )}
        </Transition>
      </MusicContainer>
    </>
  )
}

export default MusicPlayerCard
